import { useEffect, useRef, useMemo } from "react"
import {
  useLocation,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from "react-router-dom"

import moment from "moment"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { usePage } from "contexts/page-context"
import Table from "components/table"

import Filter from "pages/product/stock/filter"
import Import from "pages/product/stock/import"

import useProductUnitStockRepositories from "repositories/product-unit-stock"
import useBranchRepositories from "repositories/branch"
import { CloudDownload } from "components/icons/outline"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
  // Logged in user ability
  const { can, isSuper } = useAuth()

  // Configuration (kalau dibutuhkan)
  const { configuration } = usePage()

  // Transition
  const { title, transition, setTransition } = useOutletContext()

  // React router navigate hook
  const navigate = useNavigate()

  // React router location hook
  const location = useLocation()

  // Query parameters
  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef()

  const page = searchParams.get("page")
  const search = searchParams.get("search")
  const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
  const outlet_id = searchParams.get("outlet_id")
  const only_in_stock = searchParams.get("only_in_stock")

  const orderBy = searchParams.get("order_by")
  const direction = searchParams.get("direction")

  const {
    data: productUnitStockData,
    isLoading: isLoadingProductUnitStockData,
    mutate: mutateProductUnitStockData,
  } = useProductUnitStockRepositories({
    page,
    search,
    outlet_id,
    only_in_stock,
    order_by: orderBy,
    direction,
  })

  // outlet selection untuk tampil nama outlet + dropdown filter/import
  const { data: outletSelection, isLoading: isLoadingOutletSelection } =
    useBranchRepositories({ paginate: false })

  const outletById = useMemo(() => {
    const map = new Map()
    ;(outletSelection || []).forEach((o) => {
      map.set(o.id, o)
    })
    return map
  }, [outletSelection])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    // bersihkan null/undefined/""
    const merged = { ...current, ...next }
    Object.keys(merged).forEach((k) => {
      if (merged[k] === null || merged[k] === undefined || merged[k] === "") {
        delete merged[k]
      }
    })
    setSearchParams(merged)
  }

  useEffect(() => {
    window.history.replaceState({}, document.title)

    if (transition?.reverse) {
      setTransition({ type: "slide", reverse: false })
    }
  }, [])

  useEffect(() => {
    if (searchInput.current) {
      searchInput.current.focus()
    }
  }, [search])

  useEffect(() => {
    if (search === "") {
      navigate("", { replace: true })
    }

    if (isLoadingProductUnitStockData) {
      nProgress.start()
    } else {
      nProgress.done()
    }
  }, [productUnitStockData, isLoadingProductUnitStockData])

  // Data filter handlers
  const updateFilter = (value) => {
    // reset page saat apply filter
    mergeParams({ ...value, page: 1 })
  }

  const removeFilter = () => {
    setSearchParams({})
  }

  // Data sort handlers
  const toggleSort = (value) => {
    if ((direction === null || direction === "desc") || orderBy !== value) {
      mergeParams({ order_by: value, direction: "asc", page: 1 })
    } else {
      mergeParams({ order_by: value, direction: "desc", page: 1 })
    }
  }

  // Headers tabel
  const dataListHeaders = [
    {
      isAction: false,
      content: "Product ID",
      sortKey: "product_unit_id",
    },
    {
      isAction: false,
      content: "Nama Produk",
      // kalau backend tidak support sort relasi, fallback ini masih aman
      sortKey: "product_unit_id",
    },
    {
      isAction: false,
      content: "Outlet",
      sortKey: "outlet_id",
    },
    {
      isAction: false,
      content: "Current Stock",
      sortKey: "current_stock",
    },
    {
      isAction: false,
      content: "Purchase Date",
      sortKey: "purchase_date",
    },
    {
      isAction: false,
      content: "DOS Date",
      sortKey: "dos_date",
    },
    {
      isAction: false,
      content: "Diubah Pada",
      sortKey: "updated_at",
    },
    {
      isAction: true,
      content: null,
    },
  ]

  // Kolom tabel
  const dataListColumns = [
    // Product ID
    {
      type: "default",
      bold: true,
      content: (value) => value.product_unit_id ?? "-",
    },
    // Nama Produk (support snake_case dan camelCase)
    {
      type: "default",
      bold: true,
      content: (value) =>
        value.product_unit?.name ||
        value.productUnit?.name ||
        value.product_unit_name ||
        "-",
    },
    // Outlet
    {
      type: "default",
      bold: false,
      content: (value) => {
        const outlet = outletById.get(value.outlet_id)
        return outlet?.name ?? value.outlet?.name ?? value.outlet_name ?? "-"
      },
    },
    // Current Stock
    {
      type: "default",
      bold: true,
      content: (value) => {
        const v = value.current_stock ?? value.currentStock ?? 0
        return `${v}`
      },
    },
    // Purchase Date
    {
      type: "default",
      bold: false,
      content: (value) =>
        value.purchase_date
          ? moment(value.purchase_date).format("MMMM D, YYYY")
          : "-",
    },
    // DOS Date
    {
      type: "default",
      bold: false,
      content: (value) =>
        value.dos_date ? moment(value.dos_date).format("MMMM D, YYYY") : "-",
    },
    // Updated at
    {
      type: "default",
      bold: false,
      content: (value) =>
        value.updated_at
          ? moment(value.updated_at).format("MMMM D, YYYY")
          : "-",
    },
    // Action (sementara kosong / kamu bisa isi delete/detail nanti)
    {
      type: "action",
      content: () => <></>,
    },
  ]

  const data = productUnitStockData?.data ?? []
  const isEmpty = (data?.length ?? 0) === 0 && !search && !isLoadingProductUnitStockData
  const isEmptyOnSearching =
    (data?.length ?? 0) === 0 && !!search && !isLoadingProductUnitStockData

  const canExport = isSuper() || can('product-stock.export')

  return (
    <div className="space-y-6">
      {/* Header: search + filter + export/import */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center sm:space-x-2">
          {/* Search box */}
          <div className="relative hidden sm:block">
            <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                width={24}
                height={24}
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <circle cx={10} cy={10} r={7}></circle>
                <line x1={21} y1={21} x2={15} y2={15}></line>
              </svg>
            </div>

            <input
              ref={searchInput}
              onChange={(e) => setSearchTemp(e.target.value)}
              value={searchTemp}
              type="text"
              placeholder={`${lang.search} Product Unit Stock`}
              autoComplete="off"
              className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200"
            />
          </div>

          {/* Filter (outlet + only_in_stock) */}
          <Filter
            onSubmit={updateFilter}
            onRemove={removeFilter}
            data={Object.fromEntries(
              Object.entries({ outlet_id, only_in_stock }).filter(
                ([_, v]) => v != null && v !== ""
              )
            )}
          />

          {/* Export */}

          {/* Import */}
          {can('product-stock.import') && <Import onSuccess={() => mutateProductUnitStockData()} />}
        </div>

        {/* Right side (kosong / bisa kamu isi create kalau diperlukan) */}
        {can('product-stock.export') && <div className="flex items-center space-x-2">
          <button
            type="button"
            disabled={!canExport}
            onClick={() => {
            //   if (!outlet_id) return
              const url = `${process.env.REACT_APP_INVENTORY_API_URL}/product-unit-stocks/export?outlet_id=${encodeURIComponent(
                outlet_id
              )}`
              window.open(url, "_blank")
            }}
            className={[
              "inline-flex items-center px-4 py-3 space-x-2 text-xs transition border border-neutral-200 text-white bg-neutral-800 rounded-xl active:hover:scale-90",
              !canExport ? "opacity-50 cursor-not-allowed" : "",
            ].join(" ")}
            title={!canExport ? "Pilih outlet dulu untuk export" : "Export Excel"}
          >
          <CloudDownload className="w-4 h-4" stroke={1.5} />
            <span>Export Excel</span>
          </button></div>}
      </div>

      {/* Tabel list */}
      <Table
        isLoading={isLoadingProductUnitStockData || isLoadingOutletSelection}
        data={data}
        headers={dataListHeaders}
        columns={dataListColumns}
        key={(value) =>
          `${value.product_unit_id}-${value.outlet_id}-${value.purchase_order_id}`
        }
        isEmpty={isEmpty}
        isEmptyOnSearching={isEmptyOnSearching}
        toggleSort={toggleSort}
        orderBy={orderBy}
        direction={direction}
      />

      {/* Pagination */}
      <Pagination
        links={productUnitStockData?.links}
        from={productUnitStockData?.from}
        to={productUnitStockData?.to}
        total={productUnitStockData?.total}
      />
    </div>
  )
}
